import { useCallback, memo } from 'react';
import { noop } from 'lodash-es';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { tokens, Switch } from '@hotjar/hotsauce';
import { trackClick } from '@hotjar/webapp/features/Playbacks/utils/tracking';
import { stripDomain } from '@hotjar/webapp/utils/urls';
import { sessionsSettingsApi } from '@hotjar/webapp/features/Settings/api';
import type { Recording } from '@hotjar/webapp/features/Playbacks/types';
import { getSiteId } from '@hotjar/webapp/features/Playbacks/store/utils';
import { event as HJEvent } from '@hotjar/tracking/src/tools/hotjar';
import { useSelector, useStoreState } from '@hotjar/recordings/src/store/player/context';
import { Tile } from './Tile';

const InternalErrorTile = (): JSX.Element | null => {
  const { t } = useTranslation();
  const { openConsole } = useStoreState();

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const recording = useSelector(({ recording }) => recording);
  const errors = recording?.page_visit_recordings.reduce(
    (acc, pageVisit) => acc + pageVisit.events.filter((event) => event.type === 'error').length,
    0,
  );

  const { data: isCapturingErrors } = useQuery(
    'session-settings',
    () => sessionsSettingsApi.get(getSiteId()),
    {
      select: (response) => response?.data?.session_capture_console_consent === true,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const trackedOnClick = useCallback(() => {
    openConsole();
    trackClick(recording as Recording, {
      element_name: 'Open Console',
      ui_position: 'session_info',
      url_event_was_triggered_on: stripDomain(window.location.href),
    });

    HJEvent('Console Opened');
  }, [recording, openConsole]);

  return (
    <Tile
      icon="error"
      color={tokens.color.ember_400}
      value={
        isCapturingErrors !== undefined &&
        (isCapturingErrors ? errors : <Switch isChecked={false} onChange={noop} />)
      }
      title={t('common:player.side-panel.user-actions.errors')}
      onClick={trackedOnClick}
    />
  );
};

export const ErrorTile = memo(InternalErrorTile);
